import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    ArrowLeft, Edit, Save, X, Shield, ShieldOff, Clock,
    CheckCircle, XCircle, AlertCircle, RefreshCw, Eye,
    Mail, Phone, MapPin, Globe, Building, Briefcase,
    Calendar, Activity, Download, Share2, Loader2,
    Lock, UserCheck, ExternalLink, Copy, BadgeCheck
} from 'lucide-react';
import toast from 'react-hot-toast';
import api, { apiMethods } from '@/services/api';

const UserDetail = () => {
    const { userId } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [isEditing, setIsEditing] = useState(false);
    const [showResetModal, setShowResetModal] = useState(false);
    const [formData, setFormData] = useState({});
    const [errors, setErrors] = useState({});
    const [copied, setCopied] = useState(false);

    // Fetch user details - FIX: Access response.data.user
    const { data: user, isLoading } = useQuery({
        queryKey: ['adminUserDetail', userId],
        queryFn: async () => {
            const response = await api.get(`/admin/users/${userId}`);
            // The backend returns { success: true, user: {...} }
            return response.data.user;
        },
    });

    // React Query v5 removed onSuccess on queries, so fill the edit form when the user loads.
    useEffect(() => {
        if (!user) return;
        setFormData({
            name: user.profile?.name || '',
            email: user.email || '',
            phone: user.profile?.phone || '',
            jobTitle: user.profile?.jobTitle || '',
            organization: user.profile?.organization || '',
            biography: user.profile?.biography || '',
            isActive: user.isActive
        });
    }, [user]);

    // Update user mutation - FIX: Access response.data.user
    const updateUserMutation = useMutation({
        mutationFn: async (data) => {
            const response = await api.put(`/admin/users/${userId}`, data);
            return response.data.user;
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['adminUserDetail', userId]);
            queryClient.invalidateQueries(['adminUsers']);
            setIsEditing(false);
            setErrors({});
        },
        onError: (error) => {
            setErrors({ general: error.response?.data?.message || 'Failed to update user' });
        }
    });

    // Reset password mutation
    const resetPasswordMutation = useMutation({
        mutationFn: async () => {
            const response = await api.post(`/admin/users/${userId}/reset-password`);
            return response.data;
        },
        onSuccess: () => {
            setShowResetModal(false);
            queryClient.invalidateQueries(['adminUserDetail', userId]);
        }
    });

    // Toggle 2FA mutation - REMOVED as endpoint doesn't exist
    // You can either:
    // 1. Remove 2FA functionality from the UI
    // 2. Add the endpoint to your backend
    // For now, I'll comment it out

    const toggle2FAMutation = useMutation({
        mutationFn: async (enabled) => {
            const response = await api.post(`/admin/users/${userId}/2fa`, { enabled });
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['adminUserDetail', userId]);
        }
    });


    // Verified badge shown on the user's public card
    const verifiedMutation = useMutation({
        mutationFn: async (isVerified) => (await apiMethods.admin.setUserVerified(userId, isVerified)).data,
        onMutate: async (isVerified) => {
            const prev = queryClient.getQueryData(['adminUserDetail', userId]);
            queryClient.setQueryData(['adminUserDetail', userId], (u) => (u ? { ...u, isVerified } : u));
            return { prev };
        },
        onSuccess: (data) => {
            queryClient.setQueryData(['adminUserDetail', userId], (u) => (u ? { ...u, isVerified: data.isVerified } : u));
            queryClient.invalidateQueries({ queryKey: ['adminUsers'] });
            toast.success(data.isVerified ? 'Verified badge added' : 'Verified badge removed');
        },
        onError: (_err, _v, ctx) => {
            // The api interceptor already shows the server's error message
            if (ctx?.prev) queryClient.setQueryData(['adminUserDetail', userId], ctx.prev);
        },
    });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleToggleChange = (e) => {
        const { name, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: checked }));
    };

    const handleSave = () => {
        const updateData = {
            profile: {
                name: formData.name,
                phone: formData.phone,
                jobTitle: formData.jobTitle,
                organization: formData.organization,
                biography: formData.biography
            },
            email: formData.email,
            isActive: formData.isActive
        };
        updateUserMutation.mutate(updateData);
    };

    const handleCancel = () => {
        setIsEditing(false);
        setErrors({});
        // Reset form data
        setFormData({
            name: user?.profile?.name || '',
            email: user?.email || '',
            phone: user?.profile?.phone || '',
            jobTitle: user?.profile?.jobTitle || '',
            organization: user?.profile?.organization || '',
            biography: user?.profile?.biography || '',
            isActive: user?.isActive
        });
    };

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <Loader2 className="animate-spin h-8 w-8 text-primary-600" />
            </div>
        );
    }

    if (!user) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <AlertCircle className="h-12 w-12 text-red-600 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">User Not Found</h2>
                    <Link to="/admin/users" className="text-primary-600 hover:text-primary-700">
                        Back to Users
                    </Link>
                </div>
            </div>
        );
    }

    const getStatusBadge = () => {
        if (!user.isActive) {
            return (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                    <XCircle className="h-4 w-4 mr-1" />
                    Inactive
                </span>
            );
        }
        if (user.mustChangePassword) {
            return (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
                    <Clock className="h-4 w-4 mr-1" />
                    Password Change Required
                </span>
            );
        }
        return (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                <CheckCircle className="h-4 w-4 mr-1" />
                Active
            </span>
        );
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <Link
                        to="/admin/users"
                        className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
                    >
                        <ArrowLeft className="h-4 w-4 mr-1" />
                        Back to Users
                    </Link>

                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            {user.profile?.profilePicture ? (
                                <img
                                    src={user.profile.profilePicture}
                                    alt={user.profile.name}
                                    className="h-16 w-16 rounded-full object-cover"
                                />
                            ) : (
                                <div className="h-16 w-16 rounded-full bg-gray-200 flex items-center justify-center">
                                    <span className="text-2xl font-medium text-gray-600">
                                        {user.profile?.name?.charAt(0).toUpperCase() || 'U'}
                                    </span>
                                </div>
                            )}
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">
                                    {user.profile?.name || 'Unnamed User'}
                                </h1>
                                <p className="text-gray-600">{user.email}</p>
                            </div>
                        </div>

                        <div className="flex items-center space-x-3">
                            {!isEditing ? (
                                <>
                                    <a
                                        href={`${window.location.origin}/card/${user.cardUuid}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                                    >
                                        <ExternalLink className="h-4 w-4 mr-2" />
                                        View Card
                                    </a>
                                    <button
                                        onClick={() => setIsEditing(true)}
                                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
                                    >
                                        <Edit className="h-4 w-4 mr-2" />
                                        Edit
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button
                                        onClick={handleCancel}
                                        className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                                    >
                                        <X className="h-4 w-4 mr-2" />
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleSave}
                                        disabled={updateUserMutation.isPending}
                                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                                    >
                                        {updateUserMutation.isPending ? (
                                            <>
                                                <Loader2 className="animate-spin h-4 w-4 mr-2" />
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                <Save className="h-4 w-4 mr-2" />
                                                Save
                                            </>
                                        )}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {errors.general && (
                    <div className="mb-6 rounded-md bg-red-50 p-4">
                        <p className="text-sm text-red-800">{errors.general}</p>
                    </div>
                )}

                {/* Status and Actions */}
                <div className="bg-white rounded-lg shadow mb-6">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h2 className="text-lg font-semibold text-gray-900">Status & Actions</h2>
                    </div>
                    <div className="p-6 space-y-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-700">Account Status</p>
                                <p className="text-sm text-gray-500 mt-1">Control user access to the system</p>
                            </div>
                            {isEditing ? (
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        name="isActive"
                                        checked={formData.isActive}
                                        onChange={handleToggleChange}
                                        className="sr-only peer"
                                    />
                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                                </label>
                            ) : (
                                getStatusBadge()
                            )}
                        </div>

                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-700 flex items-center">
                                    <BadgeCheck className={`h-4 w-4 mr-1.5 ${user.isVerified ? 'text-primary-600' : 'text-gray-400'}`} />
                                    Verified badge
                                </p>
                                <p className="text-sm text-gray-500 mt-1">
                                    {user.isVerified ? 'A verified check is shown next to their name on the public card' : 'Show a verified check next to their name on the public card'}
                                </p>
                            </div>
                            <div className="flex items-center space-x-3">
                                <span className={`text-sm ${user.isVerified ? 'text-primary-700 font-medium' : 'text-gray-500'}`}>
                                    {user.isVerified ? 'Verified' : 'Not verified'}
                                </span>
                                <button
                                    type="button"
                                    role="switch"
                                    aria-checked={!!user.isVerified}
                                    aria-label="Verified badge"
                                    disabled={verifiedMutation.isPending}
                                    onClick={() => verifiedMutation.mutate(!user.isVerified)}
                                    className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-4 focus:ring-primary-300 disabled:opacity-60 ${
                                        user.isVerified ? 'bg-primary-600' : 'bg-gray-200'
                                    }`}
                                >
                                    <span
                                        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                                            user.isVerified ? 'translate-x-[22px]' : 'translate-x-[2px]'
                                        }`}
                                    />
                                </button>
                            </div>
                        </div>

                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-700">Two-Factor Authentication</p>
                                <p className="text-sm text-gray-500 mt-1">Enhanced security for user account</p>
                            </div>
                            <div className="flex items-center space-x-3">
                                {user.twoFactorEnabled ? (
                                    <Shield className="h-5 w-5 text-green-600" />
                                ) : (
                                    <ShieldOff className="h-5 w-5 text-gray-400" />
                                )}
                                {/* Commented out until 2FA endpoint is added
                                {!isEditing && (
                                    <button
                                        onClick={() => toggle2FAMutation.mutate(!user.twoFactorEnabled)}
                                        className="text-sm text-primary-600 hover:text-primary-700"
                                    >
                                        {user.twoFactorEnabled ? 'Disable' : 'Enable'}
                                    </button>
                                )}
                                */}
                                <span className="text-sm text-gray-500">{user.twoFactorEnabled ? 'Enabled' : 'Disabled'}</span>
                            </div>
                        </div>

                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-700">Password Reset</p>
                                <p className="text-sm text-gray-500 mt-1">Force user to change password on next login</p>
                            </div>
                            {!isEditing && (
                                <button
                                    onClick={() => setShowResetModal(true)}
                                    className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                                >
                                    <Lock className="h-4 w-4 mr-2" />
                                    Reset Password
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* User Information */}
                <div className="bg-white rounded-lg shadow mb-6">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h2 className="text-lg font-semibold text-gray-900">User Information</h2>
                    </div>
                    <div className="p-6">
                        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Full Name</label>
                                {isEditing ? (
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                    />
                                ) : (
                                    <p className="mt-1 text-sm text-gray-900">{user.profile?.name || '-'}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Email</label>
                                {isEditing ? (
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                    />
                                ) : (
                                    <p className="mt-1 text-sm text-gray-900">{user.email}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Phone</label>
                                {isEditing ? (
                                    <input
                                        type="tel"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                    />
                                ) : (
                                    <p className="mt-1 text-sm text-gray-900">{user.profile?.phone || '-'}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Job Title</label>
                                {isEditing ? (
                                    <input
                                        type="text"
                                        name="jobTitle"
                                        value={formData.jobTitle}
                                        onChange={handleInputChange}
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                    />
                                ) : (
                                    <p className="mt-1 text-sm text-gray-900">{user.profile?.jobTitle || '-'}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Organization</label>
                                {isEditing ? (
                                    <input
                                        type="text"
                                        name="organization"
                                        value={formData.organization}
                                        onChange={handleInputChange}
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                    />
                                ) : (
                                    <p className="mt-1 text-sm text-gray-900">{user.profile?.organization || '-'}</p>
                                )}
                            </div>

                            <div className="sm:col-span-2">
                                <label className="block text-sm font-medium text-gray-700">Biography</label>
                                {isEditing ? (
                                    <textarea
                                        name="biography"
                                        rows={3}
                                        value={formData.biography}
                                        onChange={handleInputChange}
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                    />
                                ) : (
                                    <p className="mt-1 text-sm text-gray-900">{user.profile?.biography || '-'}</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Social Media Links */}
                {user.socialMedia && Object.keys(user.socialMedia).some(key => user.socialMedia[key]) && (
                    <div className="bg-white rounded-lg shadow mb-6">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h2 className="text-lg font-semibold text-gray-900">Social Media</h2>
                        </div>
                        <div className="p-6">
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                {Object.entries(user.socialMedia).filter(([key, value]) => value).map(([platform, url]) => (
                                    <div key={platform}>
                                        <p className="text-sm font-medium text-gray-700 capitalize">{platform}</p>
                                        <a href={url} target="_blank" rel="noopener noreferrer" className="mt-1 text-sm text-primary-600 hover:text-primary-700">
                                            {url}
                                        </a>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Account Details */}
                <div className="bg-white rounded-lg shadow mb-6">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h2 className="text-lg font-semibold text-gray-900">Account Details</h2>
                    </div>
                    <div className="p-6">
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div>
                                <p className="text-sm font-medium text-gray-700">User ID</p>
                                <p className="mt-1 text-sm text-gray-900 font-mono">{user._id}</p>
                            </div>
                            <div>
                                <p className="text-sm font-medium text-gray-700">Card UUID</p>
                                <p className="mt-1 text-sm text-gray-900 font-mono">{user.cardUuid}</p>
                            </div>
                            <div>
                                <p className="text-sm font-medium text-gray-700">Created</p>
                                <p className="mt-1 text-sm text-gray-900">
                                    {new Date(user.createdAt).toLocaleString()}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm font-medium text-gray-700">Last Login</p>
                                <p className="mt-1 text-sm text-gray-900">
                                    {user.lastLogin ? new Date(user.lastLogin).toLocaleString() : 'Never'}
                                </p>
                            </div>
                            <div className="sm:col-span-2">
                                <p className="text-sm font-medium text-gray-700 mb-1">NFC Card URL</p>
                                <div className="flex items-center space-x-2">
                                    <code className="flex-1 px-3 py-2 bg-gray-100 text-sm text-gray-900 rounded font-mono">
                                        dbc.nordic3d.us/card/{user.cardUuid}
                                    </code>
                                    <button
                                        onClick={() => {
                                            navigator.clipboard.writeText(`dbc.nordic3d.us/card/${user.cardUuid}`);
                                            setCopied(true);
                                            setTimeout(() => setCopied(false), 2000);
                                        }}
                                        className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                                        title="Copy URL for NFC encoding"
                                    >
                                        {copied ? (
                                            <>
                                                <CheckCircle className="h-4 w-4 text-green-600" />
                                                <span className="ml-2 text-green-600">Copied!</span>
                                            </>
                                        ) : (
                                            <>
                                                <Copy className="h-4 w-4" />
                                                <span className="ml-2">Copy</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                                <p className="mt-1 text-xs text-gray-500">URL format for NFC chip encoding (without https://)</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Recent Login History */}
                {user.loginHistory && user.loginHistory.length > 0 && (
                    <div className="bg-white rounded-lg shadow">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h2 className="text-lg font-semibold text-gray-900">Recent Login History</h2>
                        </div>
                        <div className="p-6">
                            <div className="space-y-3">
                                {user.loginHistory.slice(0, 5).map((login, index) => (
                                    <div key={index} className="flex items-center justify-between text-sm">
                                        <div className="flex items-center space-x-3">
                                            {login.success ? (
                                                <CheckCircle className="h-4 w-4 text-green-500" />
                                            ) : (
                                                <XCircle className="h-4 w-4 text-red-500" />
                                            )}
                                            <span className="text-gray-700">
                                                {login.success ? 'Successful login' : 'Failed login attempt'}
                                            </span>
                                            {login.method && (
                                                <span className="text-xs text-gray-500">({login.method})</span>
                                            )}
                                        </div>
                                        <span className="text-gray-500">
                                            {new Date(login.timestamp).toLocaleString()}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Reset Password Modal */}
                {showResetModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-lg max-w-md w-full p-6">
                            <div className="mb-4">
                                <h3 className="text-lg font-semibold text-gray-900">Reset User Password</h3>
                                <p className="text-sm text-gray-600 mt-1">
                                    This will require the user to set a new password on their next login.
                                </p>
                            </div>

                            <div className="flex space-x-3">
                                <button
                                    onClick={() => setShowResetModal(false)}
                                    className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => resetPasswordMutation.mutate()}
                                    disabled={resetPasswordMutation.isPending}
                                    className="flex-1 px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                                >
                                    {resetPasswordMutation.isPending ? 'Resetting...' : 'Reset Password'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserDetail;